import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminSidebar from '../../components/AdminSidebar';
import NegotiationFilters from './components/NegotiationFilters';
import NegotiationTable from './components/NegotiationTable';
import NegotiationDetailPanel from './components/NegotiationDetailPanel';
import { MessageCircle } from 'lucide-react';

const AdminNegotiationsModerationDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('svi');
  const [selectedNeg, setSelectedNeg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchNegotiations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('negotiations')?.select(`
          id, status, initial_message, created_at, updated_at,
          is_archived, moderator_note, offer_id,
          buyer:user_profiles!buyer_id(id, full_name, email),
          seller:user_profiles!seller_id(id, full_name, email),
          offer:marketplace_wishes!offer_id(id, wish_title, voucher_name)
        `)?.order('created_at', { ascending: false });

      if (activeTab === 'in_progress') {
        query = query?.in('status', ['initiated', 'in_progress']);
      } else if (activeTab === 'cancelled') {
        query = query?.eq('status', 'cancelled');
      } else if (activeTab === 'completed') {
        query = query?.in('status', ['completed', 'agreed']);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const mapped = (data || [])?.map((n) => ({
        id: n?.id,
        status: n?.status,
        initialMessage: n?.initial_message,
        createdAt: n?.created_at,
        updatedAt: n?.updated_at,
        isArchived: n?.is_archived ?? false,
        moderatorNote: n?.moderator_note,
        offerId: n?.offer_id,
        offerName: n?.offer?.wish_title || n?.offer?.voucher_name || 'Oglas',
        buyerName: n?.buyer?.full_name || 'Kupac',
        buyerEmail: n?.buyer?.email || '',
        sellerName: n?.seller?.full_name || 'Prodavac',
        sellerEmail: n?.seller?.email || '',
      }));

      setNegotiations(mapped);
    } catch (err) {
      setError(err?.message || 'Greška pri učitavanju pregovora');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchNegotiations();
  }, [fetchNegotiations]);

  const filteredNegotiations = negotiations?.filter((n) => {
    if (!search) return true;
    const q = search?.toLowerCase();
    return (n?.buyerName?.toLowerCase()?.includes(q) ||
    n?.sellerName?.toLowerCase()?.includes(q) ||
    n?.offerName?.toLowerCase()?.includes(q) || n?.initialMessage?.toLowerCase()?.includes(q));
  });

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleSuspend = async (id, reason) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('negotiations')?.update({
          status: 'cancelled',
          is_archived: true,
          archived_at: new Date()?.toISOString(),
          moderator_note: reason,
        })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchNegotiations();
      setSelectedNeg(null);
      showSuccess('Pregovor je suspendovan');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (id) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('negotiations')?.update({ is_archived: false, archived_at: null })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchNegotiations();
      setSelectedNeg(null);
      showSuccess('Pregovor je vraćen');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Moderacija Pregovora</h1>
              <p className="text-sm text-gray-500">Pregled i upravljanje pregovorima na platformi</p>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
              <button onClick={() => setError(null)} className="ml-2 font-medium underline">Zatvori</button>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {successMsg}
            </div>
          )}

          {/* Main content */}
          <div className="flex gap-6">
            {/* Left: Table */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <NegotiationFilters
                  search={search}
                  onSearchChange={setSearch}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
                <p className="text-xs text-gray-500">{filteredNegotiations?.length} pregovora</p>
              </div>
              <NegotiationTable
                negotiations={filteredNegotiations}
                selectedId={selectedNeg?.id}
                onSelect={setSelectedNeg}
                loading={loading}
              />
            </div>

            {/* Right: Detail panel */}
            {selectedNeg && (
              <div className="w-96 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-shrink-0">
                <NegotiationDetailPanel
                  negotiation={selectedNeg}
                  onClose={() => setSelectedNeg(null)}
                  onSuspend={handleSuspend}
                  onRestore={handleRestore}
                  saving={saving}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNegotiationsModerationDashboard;
